import React from 'react';
import { Tooltip } from '@/Components/ui/Tooltip';

interface FormSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface FormSelectProps {
  value: string | number;
  onChange: (value: string) => void;
  options: FormSelectOption[];
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  dataTestId?: string;
  className?: string;
  icon?: React.ReactNode;
  size?: 'default' | 'sm';
  tooltip?: string;
}

/**
 * Composant Select standardisé pour les formulaires
 * Floating label (toujours flottée car le select affiche toujours une valeur)
 */
export const FormSelect: React.FC<FormSelectProps> = ({
  value,
  onChange,
  options,
  label,
  placeholder,
  required = false,
  error,
  disabled = false,
  dataTestId,
  className = '',
  icon,
  size = 'default',
  tooltip,
}) => {
  const sm = size === 'sm';
  const textClass = sm ? 'tw-text-sm' : 'tw-text-base';
  const paddingClass = label ? (sm ? 'tw-pt-4 tw-pb-1' : 'tw-pt-5 tw-pb-1.5') : (sm ? 'tw-py-1' : 'tw-py-2.5');
  const labelClass = sm ? 'tw-top-1 tw-text-[10px]' : 'tw-top-1.5 tw-text-[11px]';

  return (
    <div className={className}>
      <div className="tw-relative">
        {icon && (
          <div className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-pointer-events-none tw-z-10">
            {icon}
          </div>
        )}

        <select
          data-testid={dataTestId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          disabled={disabled}
          className={`tw-w-full ${icon ? 'tw-pl-10' : 'tw-pl-3'} tw-pr-10 ${paddingClass} ${textClass} tw-border ${sm ? 'tw-rounded-lg' : 'tw-rounded-xl'} tw-appearance-none tw-transition-all
            ${error ? 'tw-border-red-500' : 'tw-border-gray-200'}
            focus:tw-outline-none focus:tw-ring-2
            ${error ? 'focus:tw-ring-red-500' : 'focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary'}
            disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed disabled:tw-text-gray-500
            tw-bg-white`}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>

        {label && (
          <label
            className={`tw-absolute ${icon ? 'tw-left-10' : 'tw-left-3'} ${labelClass} tw-pointer-events-none tw-transition-all tw-duration-200 tw-origin-left
              ${error ? 'tw-text-red-500' : 'tw-text-affinicar-primary'}
            `}
          >
            {label}
            {!required && <span className="tw-text-gray-400 tw-font-normal tw-ml-1 tw-text-[10px]">(Optionnel)</span>}
            {tooltip && <Tooltip content={tooltip} />}
          </label>
        )}

        {/* Chevron down icon */}
        <div className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-pointer-events-none tw-text-gray-400">
          <svg
            className="tw-w-4 tw-h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {error && (
        <p className="tw-mt-1 tw-text-sm tw-text-red-600">{error}</p>
      )}
    </div>
  );
};
